import type { HairlineOptions } from "@lucasmarkes/hairline";

import { HL, type HairlineFigure } from "./kernel.js";

/**
 * Mounts a figure drawn with the `hairline-create` skill the way
 * `@lucasmarkes/hairline` mounts its own (the package's `mount.ts`), since the
 * package has no way to register a new one. Reduced motion is the kernel's:
 * its frame loop lands every spring and tween at once, and a tour leaves.
 *
 * It only ever removes what it added: the svg, and the attributes the host did
 * not already have.
 */

/** The package's options; a key given as `undefined` goes back to its default, as the package's `update` does. */
export type FigureOptions = { [K in keyof HairlineOptions]?: HairlineOptions[K] | undefined };
/** The package's `Figure`, taking those options. */
export type MountedFigure = { update(options: FigureOptions): void; destroy(): void };

const NS = "http://www.w3.org/2000/svg";
const REST = "rest";
const mounted = new WeakMap<Element, () => void>();

/** An error from a consumer's callback, reported as uncaught without unwinding the frame loop every figure shares. */
const report = (err: unknown) => {
  if (typeof reportError === "function") reportError(err);
  else setTimeout(() => { throw err; });
};

/** The engine's own number for an intensity: two straight lines that meet at 0.5, as the package's `parameter`. */
export function parameter(range: HairlineFigure["range"], value: unknown): number {
  const n = typeof value === "string" && value.trim() !== "" ? Number(value) : value;
  const i = typeof n === "number" && Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0.5;
  const [lo, mid, hi] = range;
  const v = i <= 0.5 ? lo + (i / 0.5) * (mid - lo) : mid + ((i - 0.5) / 0.5) * (hi - mid);
  return Math.round(v * 1000) / 1000;
}

export function mountFigure(figure: HairlineFigure, el: HTMLElement, options?: FigureOptions): MountedFigure {
  if (typeof document === "undefined") {
    throw new Error(`hairline: ${figure.name} needs a DOM. Mount it in an effect, once the element exists.`);
  }
  mounted.get(el)?.();

  const opts: FigureOptions = { ...options };
  const root = el.getRootNode();
  HL.inject(root.nodeType === 9 || "host" in root ? (root as Document | ShadowRoot) : el.ownerDocument);

  const owned = new Set<string>();
  const attr = (name: string, value: string | null) => {
    if (!owned.has(name) && el.hasAttribute(name)) return;
    if (value === null) { el.removeAttribute(name); owned.delete(name); }
    else { el.setAttribute(name, value); owned.add(name); }
  };
  const dress = () => {
    attr("data-hairline-theme", opts.theme === "light" || opts.theme === "dark" ? opts.theme : null);
    attr("aria-label", el.hasAttribute("aria-labelledby") ? null : typeof opts.label === "string" ? opts.label : figure.means);
  };
  attr("data-hairline", figure.name);
  attr("role", "img");
  dress();

  const svg = el.ownerDocument.createElementNS(NS, "svg");
  svg.setAttribute("viewBox", "0 0 400 320");
  svg.setAttribute("aria-hidden", "true");
  el.appendChild(svg);

  /* engines write the read-out every frame, so only a change goes any further */
  let text: string | null = null;
  const read = {
    get textContent() { return text; },
    set textContent(value: string | null) {
      const next = value ?? "";
      if (next === text) return;
      text = next;
      const fn = opts.onRead;
      if (typeof fn === "function") try { fn(next); } catch (err) { report(err); }
    },
  };

  const stops = figure.tour ?? HL.LAP;
  let value = parameter(figure.range, opts.intensity);
  /* the tour registers before the engine, so its board ticks first in a frame */
  let playing = opts.play === true ? HL.tour(el, stops) : null;
  let engine: ReturnType<HairlineFigure["mount"]>;
  try { engine = figure.mount({ stage: el, svg, read }, value); } catch (err) { playing?.stop(); throw err; }
  if (text === null) read.textContent = REST;

  let dead = false;
  const destroy = () => {
    if (dead) return;
    dead = true;
    if (mounted.get(el) === destroy) mounted.delete(el);
    playing?.stop();
    playing = null;
    engine.destroy();
    svg.remove();
    for (const name of owned) el.removeAttribute(name);
    owned.clear();
  };
  mounted.set(el, destroy);

  return {
    update(next) {
      if (dead || !next) return;
      const own = opts as Record<string, unknown>, given = next as Record<string, unknown>;
      for (const k in given) {
        if (given[k] === undefined) delete own[k];
        else own[k] = given[k];
      }
      const v = parameter(figure.range, opts.intensity);
      if (v !== value) { value = v; engine.set(v); }
      if ((opts.play === true) !== !!playing) {
        playing?.stop();
        playing = opts.play === true ? HL.tour(el, stops) : null;
      }
      dress();
    },
    destroy,
  };
}
